import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, PhoneCall, Truck, ChevronRight, ChevronDown } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShopOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', dropdown: true },
    { name: 'About', path: '/about' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Calm utility bar */}
      <div className="bg-dark-900 text-dark-300 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="flex items-center gap-2 tracking-wide">
            <Truck className="w-3.5 h-3.5 text-athletic-400" />
            <span className="text-white font-medium">Free USPS shipping</span>
            <span className="hidden sm:inline">&middot; 2&ndash;4 day delivery on every US order</span>
          </p>
          <div className="flex items-center gap-4 tracking-wide">
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden md:inline hover:text-white transition-colors">{CONTACT_INFO.email}</a>
            <a href={phoneHref} className="flex items-center gap-1.5 font-medium text-white hover:text-athletic-400 transition-colors">
              <PhoneCall className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-white/90 backdrop-blur border-b border-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[74px] gap-4">
            <Logo />

            {/* Desktop primary nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                if (link.dropdown) {
                  return (
                    <div
                      key={link.path}
                      className="relative"
                      onMouseEnter={() => setShopOpen(true)}
                      onMouseLeave={() => setShopOpen(false)}
                    >
                      <Link
                        to={link.path}
                        className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'text-athletic-700 bg-athletic-50' : 'text-dark-600 hover:text-athletic-700 hover:bg-dark-50'}`}
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${shopOpen ? 'rotate-180' : ''}`} />
                      </Link>
                      {/* Mega submenu */}
                      {shopOpen && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[560px]">
                          <div className="bg-white rounded-2xl border border-dark-100 shadow-soft p-3 grid grid-cols-2 gap-1.5">
                            <Link to="/shop" className="col-span-2 flex items-center justify-between px-4 py-3 rounded-xl bg-dark-900 text-white hover:bg-dark-800 transition-colors">
                              <span className="font-semibold text-sm">Shop all products</span>
                              <ChevronRight className="w-4 h-4 text-athletic-400" />
                            </Link>
                            {categories.map((cat) => (
                              <Link
                                key={cat.id}
                                to={`/shop?category=${cat.slug}`}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-dark-50 transition-colors group"
                              >
                                <span className="w-11 h-11 rounded-lg overflow-hidden bg-dark-50 flex-shrink-0 border border-dark-100">
                                  <img src={cat.image} alt="" className="w-full h-full object-cover" />
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-dark-800 group-hover:text-athletic-700 truncate">{cat.name}</span>
                                  <span className="block text-xs text-dark-400">{cat.productCount} item{cat.productCount !== 1 ? 's' : ''}</span>
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'text-athletic-700 bg-athletic-50' : 'text-dark-600 hover:text-athletic-700 hover:bg-dark-50'}`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <a href={phoneHref} className="hidden xl:flex items-center gap-2 px-4 py-2.5 rounded-full border border-dark-200 text-dark-700 text-sm font-medium hover:border-athletic-400 hover:text-athletic-700 transition-colors">
                <PhoneCall className="w-4 h-4 text-athletic-600" /> Talk to us
              </a>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-full flex items-center justify-center text-dark-800 hover:text-athletic-700 bg-dark-50 hover:bg-athletic-50 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-athletic-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center text-dark-800 bg-dark-50 hover:bg-dark-100"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between px-4 py-3 text-base font-medium text-dark-700 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl"
                >
                  {link.name}
                  <ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-100 mt-3">
                <p className="px-4 text-xs uppercase tracking-wider text-dark-400 mb-2">Shop by category</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-xl bg-dark-900 text-white font-medium">
                <PhoneCall className="w-4 h-4 text-athletic-400" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
